// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import resetCheckoutState from "@bg/App/Handlers/OnePageCheckout/ResetCheckoutState";
import Modals from "@bg/Overrides/Components/Modals";
import AssociatedSalesRepModal from "@bgs/App/Components/Cart/AssociatedSalesRepModal";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import {
    getCurrentCartState,
    getReviewAndPayCartState,
    isCartLoadedWithoutLines,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getCurrentPage, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/LoadDataIfNeeded";
import setCartId from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetCartId";
import setInitialValues from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetInitialValues";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { createContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = getReviewAndPayCartState(state);
    const { paymentDetailsState } = state.pages.onePageCheckout;
    const promotionsDataView = getReviewAndPayPromotions(state);
    const location = getCurrentPage(state);
    const expand = state.data.carts.cartApiParameter?.expand || [];

    const isLoadDataNeeded =
        ((isCartLoadedWithoutLines(cartState) ||
            !cartState.value?.carriers ||
            !expand.includes("shipping") ||
            !expand.includes("tax")) &&
            !cartState.isLoading) ||
        (!promotionsDataView.value && !promotionsDataView.isLoading) ||
        !getAddressFieldsDataView(state).value;

    return {
        cartId,
        cartState,
        isLoadDataNeeded: isLoadDataNeeded || !getCurrentCountries(state),
        isUpdatingCustomer: state.pages.onePageCheckout.isUpdatingCustomer,
        shouldLoadAccount: !getCurrentAccountState(state).value,
        location,
        paymentDetailsState,
        // XNG-Changes
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
        isLoading:
            !cartState.value ||
            cartState.isLoading ||
            state.pages.onePageCheckoutConfirmation.isPreloadingData ||
            state.pages.onePageCheckout.isSavingShipping ||
            state.pages.checkoutReviewAndSubmit.isApplyingPromotion,
        cartEmpty: !cartState.isLoading && cartState.value && !cartState.value.cartLines?.length,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        // XNG-Changes
        associatedSales: getCurrentCartState(state).value?.properties.associatedSales,
        associatedSalesRepNumber: getCurrentCartState(state).value?.properties.associatedSalesRepNumber,
    };
};

const mapDispatchToProps = {
    loadDataIfNeeded,
    setInitialValues,
    setCartId,
    loadCurrentAccount,
    setPaymentDetailsState,
    // XNG-Changes
    resetCheckoutState,
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const OnePageCheckoutFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
});

const OnePageCheckoutPage = ({
    id,
    cartId,
    cartState,
    isLoadDataNeeded,
    isUpdatingCustomer,
    shouldLoadAccount,
    loadDataIfNeeded,
    setInitialValues,
    setCartId,
    loadCurrentAccount,
    location,
    paymentDetailsState,
    setPaymentDetailsState,
    // XNG-Changes
    isLoading,
    isPlacingOrder,
    cartEmpty,
    cartPageUrl,
    resetCheckoutState,
    associatedSales,
    associatedSalesRepNumber,
}: Props) => {
    const history = useHistory();
    const firstLoad = useRef(true);

    const [state] = useState<State>({
        formSubmitAttempted: false,
        validators: {},
    });

    useEffect(() => {
        if (isLoadDataNeeded && !firstLoad.current) {
            loadDataIfNeeded({ cartId, onSuccess: () => setInitialValues() });
        }
    });

    useEffect(() => {
        setCartId({ cartId });
        /**
         * XNG-Changes
         * BG-2851
         * Call Cart request everytime to handle cache issue
         */
        // if (!isLoadDataNeeded) {
        //     setInitialValues();
        // }
        loadDataIfNeeded({
            cartId,
            forceRequest: true,
            onSuccess: () => {
                setInitialValues();
                firstLoad.current = false;
            },
        } as any);

        if (shouldLoadAccount) {
            loadCurrentAccount();
        }
        return () => {
            resetCheckoutState();
        };
    }, []);

    useEffect(() => {
        if (cartEmpty && !isPlacingOrder) {
            history.push(cartPageUrl || "/", { passModal: true });
        }
    }, [cartEmpty]);

    // XNG-Changes
    // useEffect(() => {
    //     if (paymentDetailsState === "complete") {
    //         setPaymentDetailsState({ paymentDetailsState: "active" });
    //     }
    // }, [location]);

    return (
        <Page>
            <LoadingOverlay id="OnePageCheckoutPage" loading={isUpdatingCustomer || isLoading || isPlacingOrder}>
                <OnePageCheckoutFormContext.Provider value={state}>
                    <Zone zoneName="Content" contentId={id} />
                </OnePageCheckoutFormContext.Provider>
            </LoadingOverlay>
            {/* XNG-Changes */}
            <AssociatedSalesRepModal
                disabled={!associatedSales || isUpdatingCustomer || isLoading || !cartState?.value}
                associatedSales={associatedSales && JSON.parse(associatedSales)}
                associatedSalesRepNumber={associatedSalesRepNumber}
            />
            <Modals />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;
